import { 
	current,
	createSlice,
	createSelector,
	createAsyncThunk,
	createEntityAdapter
} from '@reduxjs/toolkit'

import {
	envAwareFormSubmit,
	envAwarePost,
	dateToAPIStandard
} from '../../util/util'

export const reactToChatMessage = createAsyncThunk(
	'messaging/reactToChatMessage',
	async payload => await envAwarePost('api/messaging/api/',{
		action:'reactToChatMessage',
		...payload
	})
)

export const toggleSaveMemo = createAsyncThunk(
	'messaging/toggleSaveMemo',
	async payload => await envAwarePost('api/messaging/api/',{
		action:'toggleSaveMemo',
		...payload
	})
)

export const deleteChatRoomMessage = createAsyncThunk(
	'messaging/deleteChatRoomMessage',
	async id => await envAwarePost('api/messaging/api/',{
		action:'deleteChatRoomMessage',id
	})
)

export const getChatRooms = createAsyncThunk(
	'getChatRooms',
	async () => {
		const res = await envAwarePost(
			'api/messaging/api/',
			{action:'getChatRooms'}
		)
		//attach empty array of messages to each room
		if(res.success === '1'){
			Object.values(res.data.room_map).forEach(room=>{
				room.messages = []
			})
		}
		return res
	}
)

export const getContacts = createAsyncThunk(
	'getContacts',
	async () => await envAwarePost(
		'api/messaging/api/',
		{action:'getContacts'}
	)
)

export const createChatRoom = createAsyncThunk(
	'createChatRoom',
	async payload => await envAwarePost(
		'api/messaging/api/',
		{
			action:'createChatRoom',
			...payload
		}
	)
)

export const removeMembersFromChatRoom = createAsyncThunk(
	'removeMembersFromChatRoom',
	async payload => await envAwarePost(
		'api/messaging/api/',
		{
			action:'removeMembersFromChatRoom',
			...payload
		}
	)
)

export const updateChatRoomName = createAsyncThunk(
	'updateChatRoomName',
	async payload => await envAwarePost(
		'api/messaging/api/',
		{
			action:'updateChatRoomName',
			...payload
		}
	)
)

export const deleteChatRoom = createAsyncThunk(
	'deleteChatRoom',
	async payload => await envAwarePost(
		'api/messaging/api/',
		{
			action:'deleteChatRoom',
			...payload
		}
	)
)

export const addMembersToChatRoom = createAsyncThunk(
	'addMembersToChatRoom',
	async payload => await envAwarePost(
		'api/messaging/api/',
		{
			action:'addMembersToChatRoom',
			...payload
		}
	)
)

export const getChatRoom = createAsyncThunk(
	'getChatRoom',
	async (payload,{getState,dispatch}) => {
		const state = getState()
		const user_id_number = state.users.current_user.user_id_number
		const room_id = payload.room_id
		if(Boolean(room_id)){
			//if room_id not specified the below 3 lines will crash
			const newMessageCount = selectUserRoomNewMessageCount(state,user_id_number,room_id)
			dispatch(decrementTotalNewMessageCount(newMessageCount))
			dispatch(resetNewMessageCount({user_id_number,room_id}))
		}
		dispatch(dispatch(setInReferenceToResource({
			type:'NONE',
			id:null,
			resource:null
		})))
		return await envAwarePost(
			'api/messaging/api/',
			{
				action:'getChatRoom',
				...payload
			}
		)
	}
)

export const createChatMessage = createAsyncThunk(
	'createChatMessage',
	async (payload,{getState}) => await envAwareFormSubmit({
		path:'api/messaging/api/',
		payload:{
			action:'createChatMessage',
			...payload
		}
	})
)

export const createChatResourceReference = createAsyncThunk(
	'createChatResourceReference',
	async (payload,{dispatch}) => {
		const {data:{reference}} = await envAwarePost(
			'api/messaging/',
			{action:'createChatResourceReference',...payload}
		)
		dispatch(setRoomsCollapse(true))
		return dispatch(getChatRoom({room_id:reference.room_id}))
		.then(()=>{
			dispatch(setInReferenceToResource({
				type:reference.resource_type,
				id:reference.id,
				resource:reference.resource
			}))
			dispatch(setShow(true))
		})
	}
)

export const removeChatResourceReference = createAsyncThunk(
	'removeChatResourceReference',
	async (id) => await envAwarePost(
		'api/messaging/',
		{action:'removeChatResourceReference',id}
	)
)

export const scheduleMessage = createAsyncThunk(
	'scheduleMessage',
	async payload => await envAwarePost('api/messaging/api/',{
		action:'scheduleMessage',
		...payload
	})
)

export const loadMoreMessages = createAsyncThunk(
	'getChatroom',
	async payload => await envAwarePost('api/messaging/api/',{
		action:'getChatRoom',
		...payload
	})
)

const slice = createSlice({
	name: 'messageCenter',
	initialState : {
		show : false,
		fullscreen: false,
		//ROOM | GROUP
		mode:'ROOM',
		prevMode:'ROOM',
		totalNewMessageCount:0,
		contacts : {
			status:'idle',
			byType:{}
		},
		rooms : {
			status:'idle',
			typeMap:{},
			roomMap:{},
			memberRoomMap:{},
			filters:{
				query:'',
				newOnly:false
			},
			collapse:window.innerWidth < 1000
		},
		activeRoom : {
			status:'idle',
			inReferenceToResource:{
				type:'NONE',
				id:null,
				resource:null
			},
			id:null
		}, 
		resourceReferenceCreator : {
			resource:{
				type:'NONE',
				id:null,
				name:null
			},
			show:false
		}
	},
	reducers : {
		setShow : (state,action) => { 
			state.show = action.payload 
		},
		setInReferenceToResource : (state,action) => {
			const {type,id,resource} = action.payload
			state.activeRoom.inReferenceToResource.resource = resource
			state.activeRoom.inReferenceToResource.type = type
			state.activeRoom.inReferenceToResource.id = id
		},
		setReferenceCreator : (state,action) => {
			state.resourceReferenceCreator.show = action.payload.show
			state.resourceReferenceCreator.resource = action.payload.resource
		},
		setMode : (state,action) => { 
			//prevMode is only for canceling create room
			//so if current mode is CREATE_ROOM don't set prevMode
			if(state.mode!=='CREATE_ROOM') state.prevMode = state.mode
			state.mode = action.payload 
		},
		setRoomsCollapse : (state,action) => {
			state.rooms.collapse = action.payload
		},
		setFullscreen : (state,action) => {
			state.fullscreen = action.payload
		},
		resetNewMessageCount : (state,action)=>{
			const {user_id_number,room_id} = action.payload
			state.rooms.roomMap[room_id].members_map[user_id_number].new_message_count = 0
		},
		decrementTotalNewMessageCount : (state,action)=>{
			state.totalNewMessageCount -= action.payload
		},
		setQuery : (state,action)=>{
			state.rooms.filters.query = action.payload
		},
		setNewOnly : (state,action) => {
			state.rooms.filters.newOnly = action.payload
		},
		chatRoomPushNoteReceived : (state,action) => {
			const {body,upload_url,sender_id,room_id,recipient_id,reactions,message_id} = action.payload
			const sent_at =  dateToAPIStandard(new Date())
			if(state.activeRoom.id===room_id&&state.show){
				const activeRoom = state.rooms.roomMap[state.activeRoom.id]
				
				const tmpMessage = {
					sent_at,
					author_id: parseInt(sender_id),
					attachment:upload_url,
					id:message_id,
					message:body,
					room_id,
					reactions
				}
				activeRoom.messages.push(tmpMessage)
				activeRoom.last_message_received_at = sent_at
			}else{
				const room = state.rooms.roomMap[room_id]
				if(Boolean(room)){
					room.last_message_received_at = sent_at
					room.members_map[recipient_id].new_message_count += 1
					state.totalNewMessageCount += 1
				}
			}
			
		},
		chatRoomMessageReactionReceived: (state,action)=>{
			const {reactions,message_id,room_id} = action.payload
			const room = state.rooms.roomMap[room_id]
			if (!Boolean(room)) return 
			const message = room.messages.find(m=>m.id===message_id)
			if (Boolean(message)) message.reactions.map = reactions.map
		}
	},
	extraReducers : {
		//CONTACTS
		[getContacts.pending] : state => { state.contacts.status = 'pending' },
		[getContacts.fulfilled] : (state,action) => { 
			state.contacts.status = 'idle' 
			const {success,data,message} = action.payload
			if(success==='1'){
				state.contacts.byType = data.contacts_by_type
			}else{
				alert(message)
			}
		},
		//CREATE ROOM 
		[createChatRoom.fulfilled] : (state,action) => {
			const {success,data} = action.payload
			if(success==='1'){
				const chat_room = data.chat_room
				state.mode = 'ROOM'
				//insert into state
				state.rooms.roomMap[chat_room.id] = chat_room
				state.rooms.typeMap.custom.push(chat_room.id)
				//update active room
				state.activeRoom.id = chat_room.id
			}
		},
		//ROOMS
		[getChatRooms.pending] : state => { 
			state.rooms.status = 'pending' 
		},
		[getChatRooms.fulfilled] : (state,action) => {
			state.rooms.status = 'idle'
			const {success,data,message} = action.payload
			if( success === '1' ){
				state.rooms.typeMap = data.type_map
				state.rooms.roomMap = data.room_map
				state.rooms.memberRoomMap = data.member_room_map
				state.totalNewMessageCount = data.total_new_message_count
			} else {
				window.alert( message )
			}
		},
		//ACTIVE ROOM
		[getChatRoom.pending] : (state,action) => { 
			const {room_id} = action.meta.arg
			state.prevMode = state.mode
			state.mode = 'ROOM'
			state.activeRoom.status = 'pending' 
			state.activeRoom.id = room_id
		},
		[getChatRoom.fulfilled] : (state,action)=>{
			const {success,data,message} = action.payload
			if (success==='1') {
				const {chat_room} = data
				//sometimes a chatroom will be null but a success of 1
				//catch this here
				if(Boolean(chat_room)){
					//a user can trigger several api calls by clicking rapidly
					//between chat rooms in the side menu. To prevent this
					//from affecting state/UI only update state if the data
					//we get from the api is the same as the one we have set
					//in the active room
					if ( state.activeRoom.id !== chat_room.id ) return
					state.activeRoom.status = 'idle'
					state.rooms.roomMap[chat_room.id].messages = chat_room.messages
				}else{
					window.alert('There was an error loading the chat room. Try reloading the page. If this continues contact support@fitsw.com')
					state.activeRoom.status = 'idle'
				}
			} else {
				state.activeRoom.status = 'idle'
				window.alert(message)
			}
		},
		[createChatMessage.pending] : (state,action) => {
			const {message,attachment,author_id,media_type,reactions,is_reference,reference_id} = action.meta.arg
			const tmpMessage = {
				id:`tmp_id_${Date.now()}`,
				message_id:0,
				sent_at: dateToAPIStandard(new Date()),
				author_id: parseInt(author_id),
				attachment,
				room_id:state.activeRoom.id,
				is_reference,
				reference_id,
				media_type,
				message,
				reactions
			}
			state.rooms.roomMap[state.activeRoom.id].messages.push(tmpMessage)
		},
		[createChatMessage.fulfilled] : (state,action) => {
			const {success,data,message} = action.payload
			if(success==='1'){
				const {chat_room} = data
				state.rooms.roomMap[state.activeRoom.id].messages = chat_room.messages
			}else{
				window.alert(message)
			}
		},
		[loadMoreMessages.fulfilled] : (state,action) =>{
			const {success,data,message} = action.payload
			if(success==='1'){
				state.rooms.roomMap[state.activeRoom.id].messages = data.chat_room.messages
			}else{
				window.alert(message)
			}
		},
		[removeMembersFromChatRoom.pending] : (state,action) => {
			const {group_ids,room_id} = action.meta.arg
			const groupSet = new Set(group_ids)
			const room = state.rooms.roomMap[room_id]
			const {messages} = room
			room.messages = messages.filter(message=>!groupSet.has(message.author_id))

		},
		[removeMembersFromChatRoom.fulfilled] : (state,action) => {
			const {success,data} = action.payload
			if(success === '1'){
				const chat_room = data.chat_room
				state.rooms.roomMap[chat_room.id] = chat_room
			}
		},
		[addMembersToChatRoom.fulfilled] : (state,action) => {
			const {success,data} = action.payload
			if(success === '1'){
				const chat_room = data.chat_room
				state.rooms.roomMap[chat_room.id] = chat_room
			}
		},
		[deleteChatRoom.pending] : (state,action) => {
			const {room_id} = action.meta.arg
			state.activeRoom.id = null
			//remove from room map
			const roomMap = state.rooms.roomMap
			const update = {}
			Object.entries(roomMap).forEach(([k,v])=>{
				if(k!==room_id) update[k] = v
			})
			state.rooms.roomMap = update
			//remove from type map
			for(let type in state.rooms.typeMap){
				state.rooms.typeMap[type] = state.rooms.typeMap[type].filter(id=>id!==room_id)
			}
		},

		[updateChatRoomName.pending] : (state,action)=>{
			const {room_id,name} = action.meta.arg
			state.rooms.roomMap[room_id].name = name
		},

		[deleteChatRoomMessage.pending] : (state,action)=>{
			const id = action.meta.arg
			const room = state.rooms.roomMap[state.activeRoom.id]
			room.messages = room.messages.filter( m => m.id !== id )
		},

		[removeChatResourceReference.pending] : (state,action)=>{
			const id = action.meta.arg
			const room = state.rooms.roomMap[state.activeRoom.id]
			room.messages = room.messages.filter( m => !m.is_resource_reference || m.id !== id )
		},

		[reactToChatMessage.pending] : (state,action)=>{
			const {room_id,message_id,reaction_id,user_id_number} = action.meta.arg
			const room = state.rooms.roomMap[room_id]
			const message = room.messages.find(m=>m.id===message_id)
			message.reactions.map[reaction_id].count += 1
			if(message.reactions.author_reaction_id){
				message.reactions.map[message.reactions.author_reaction_id].count -= 1
			}
			message.reactions.author_reaction_id = reaction_id
		},

		[toggleSaveMemo.pending] : (state,action)=>{
			const {room_id,message_id,save_memo} = action.meta.arg
			const room = state.rooms.roomMap[room_id]
			const message = room.messages.find(m=>m.id===message_id)
			message.save_memo = save_memo
		}
	}
})

export const {
	chatRoomMessageReactionReceived,
	decrementTotalNewMessageCount,
	chatRoomPushNoteReceived,
	setInReferenceToResource,
	resetNewMessageCount,
	setReferenceCreator,
	setRoomsCollapse,
	setFullscreen,
	setNewOnly,
	setQuery,
	setMode,
	setShow
} = slice.actions

export const selectRoomsCollapse = state => state.messageCenter.rooms.collapse

export const selectFullscreen = state => state.messageCenter.fullscreen

export const selectShow = state => state.messageCenter.show

export const selectMode = state => state.messageCenter.mode

export const selectPrevMode = state => state.messageCenter.prevMode

export const selectRoomsStatus = state => state.messageCenter.rooms.status

export const selectUserRoomNewMessageCount = (state,user_id_number,room_id)=>(
	state.messageCenter.rooms.roomMap[room_id].members_map[user_id_number].new_message_count
)

export const selectContacts = state => state.messageCenter.contacts

export const selectFilters = state => state.messageCenter.rooms.filters

export const selectMemberPrivateRoomId = (state,memberId)=>(
	state.messageCenter.rooms.memberRoomMap[memberId]
)

export const selectMemberRoomNewMessageCount = (state,roomId)=>{
	const memberId = state.users.current_user.user_id_number
	const room = state.messageCenter.rooms?.roomMap[roomId]
	const member = room?.members_map[memberId]
	return member?.new_message_count
}

export const selectFilteredRoomsByType = state => {
	const user_id_number = state.users.current_user.user_id_number
	const {filters:{query,newOnly},typeMap,roomMap} = state.messageCenter.rooms
	const roomsByType = {}
	Object.entries(typeMap).forEach(([type,ids])=>{
		const matches = ids.map(id=>roomMap[id]).filter(room=>{
			const othersMembers = room.others.map(other=>room.members_map[other])
			let membersMatchQuery = false
			for (var i = othersMembers.length - 1; i >= 0; i--) {
				if (!othersMembers[i].user_name) {
					break;
				}
				const uname = othersMembers[i].user_name.toLowerCase()
				const lowq = query.toLowerCase()
				if( uname.includes(lowq) ){
					membersMatchQuery = true
					break
				}
			}
			const roomNameMatchesQuery = (
				type === 'custom' 
				&& room.name.toLowerCase().includes(query.toLowerCase())
			)
			const queryMatch = !Boolean(query) || membersMatchQuery || roomNameMatchesQuery
			const newOnlyMatch = !newOnly || room.members_map[user_id_number].new_message_count > 0
			return queryMatch && newOnlyMatch
		})
		//don't display groups with no matches
		//but always display custom room group
		if(matches.length||type==='custom') roomsByType[type] = matches
	})
	return roomsByType
}

export const selectOtherRoomMembers = state => {
	const others = state.messageCenter.activeRoom.others
	if(!Boolean(others)) return []
	const map = state.messageCenter.activeRoom.membersMap
	return others.map(id=>map[id])
}

export const selectTotalNewMessageCount = state => state.messageCenter.totalNewMessageCount

export const selectActiveRoom = state => {
	const status = state.messageCenter.activeRoom.status
	const room = state.messageCenter.rooms.roomMap[state.messageCenter.activeRoom.id]
	//filter messages if discussing some resource
	const inReferenceToResource = state.messageCenter.activeRoom.inReferenceToResource
	if(inReferenceToResource.type!=='NONE'){
		const filteredRoom = {...room}
		filteredRoom.messages = room.messages.filter(m=>{
			if(m.is_resource_reference){
				return m.id === inReferenceToResource.id
			}else{
				return m.reference_id === inReferenceToResource.id
			}
		})
		return {
			status,
			...filteredRoom
		}
	}else{
		return (
			{
				status,
				...room
			}
		)
	}
	
}

export const selectInReferenceToResource = state => state.messageCenter.activeRoom.inReferenceToResource

export const selectAllClientContacts = state=>(
	state.messageCenter.contacts.byType.client
)

export default slice.reducer